import { useTheme } from 'emotion-theming';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex, Text } from 'rebass';
import type { Theme } from '../../theme';
import IconLink from '../Icon/IconLink';
import StyledLink from '../Link/StyledLink';
import AnchorMenu from '../Menu/AnchorMenu';
import Spinner from '../Spinner/Spinner';
import useMode from '../../hooks/useMode';
import ActivityFeedList from './ActivityFeedList';
import useActivityFeed from './useActivityFeed';
import { ACTIVITY_ITEMS_UPDATED_EVENT } from './ActivityHandler';

const ActivityMenu = ({
  menuDomId,
  limit = 15,
  panelWidthPx = 380,
  caretEdgeInset = 20,
  placement = 'top',
}: {
  menuDomId: string;
  limit?: number;
  panelWidthPx?: number;
  caretEdgeInset?: number;
  placement?: 'top' | 'top-right';
}): JSX.Element => {
  const mode = useMode();
  const theme = useTheme() as Theme;
  const { data, items, loading, error, refetch, hasUnread, hasFollowUp } =
    useActivityFeed(limit);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [autoFocusFirstItem, setAutoFocusFirstItem] = useState(false);

  const closeMenu = useCallback(() => {
    setAutoFocusFirstItem(false);
    setOpen(false);
  }, []);
  const toggleMenu = useCallback(() => {
    setAutoFocusFirstItem(false);
    setOpen((value) => !value);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    void refetch();
  }, [open, refetch]);

  useEffect(() => {
    const handleActivityItemsUpdated = () => {
      void refetch();
    };

    window.addEventListener(
      ACTIVITY_ITEMS_UPDATED_EVENT,
      handleActivityItemsUpdated
    );
    return () => {
      window.removeEventListener(
        ACTIVITY_ITEMS_UPDATED_EVENT,
        handleActivityItemsUpdated
      );
    };
  }, [refetch]);

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setAutoFocusFirstItem(true);
          setOpen(true);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          setAutoFocusFirstItem(false);
          toggleMenu();
          break;
        case 'Escape':
          if (open) {
            event.preventDefault();
            closeMenu();
          }
          break;
      }
    },
    [closeMenu, open, toggleMenu]
  );

  useEffect(() => {
    if (!open) {
      if (wasOpenRef.current) {
        triggerRef.current?.focus();
      }
      wasOpenRef.current = false;
      return;
    }
    wasOpenRef.current = true;
  }, [open]);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        overflow: 'visible',
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuDomId}
        aria-label="Activity"
        title="Activity"
        onClick={toggleMenu}
        onKeyDown={handleTriggerKeyDown}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 0,
          cursor: 'pointer',
          border: 'none',
          background: 'transparent',
          padding: 0,
        }}
      >
        <IconLink as="span" icon="bell" iconAtEnd isDark />
        {(hasUnread || hasFollowUp) && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: 8,
              height: 8,
              borderRadius: 'circle',
              bg: hasUnread ? 'primary' : 'statusOrange',
              borderStyle: 'solid',
              borderWidth: '2px',
              borderColor: 'navbar',
            }}
          />
        )}
      </button>
      {open && (
        <AnchorMenu
          id={menuDomId}
          triggerRef={triggerRef}
          ariaLabel="Activity"
          title={
            <Flex
              alignItems="center"
              justifyContent="space-between"
              sx={{ gap: 2 }}
            >
              <Flex alignItems="center" sx={{ gap: 2, minWidth: 0 }}>
                <Text
                  variant="body2"
                  fontWeight="bold"
                  color="text"
                  sx={{ minWidth: 0 }}
                >
                  Activity
                </Text>
                {loading && (
                  <Box
                    as="span"
                    aria-hidden
                    sx={{
                      flexShrink: 0,
                      lineHeight: 0,
                      display: 'inline-flex',
                    }}
                  >
                    <Spinner
                      size={14}
                      color={theme.colors.primary}
                      backgroundColor={theme.colors.base.gray[200]}
                    />
                  </Box>
                )}
              </Flex>
              <StyledLink
                href={`/${mode}/activity`}
                sx={{ fontSize: 0, lineHeight: 'interactive' }}
              >
                See all
              </StyledLink>
            </Flex>
          }
          panelWidthPx={panelWidthPx}
          placement={placement}
          caretEdgeInset={caretEdgeInset}
          layoutDependency={[items.length, loading, error?.message].join(',')}
          outsidePointerBoundaryRef={rootRef}
          onDismiss={closeMenu}
        >
          <ActivityFeedList
            items={items}
            loading={loading}
            error={error}
            hasData={!!data}
            emptyMessage="No notifications yet."
            variant="menu"
            autoFocusFirstItem={autoFocusFirstItem}
            onRequestClose={closeMenu}
            returnFocusRef={triggerRef}
          />
        </AnchorMenu>
      )}
    </Box>
  );
};

export default ActivityMenu;
